import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { EyeOff, Layers, X } from "lucide-react";
import L from "leaflet";
import apiClient from "@/api/client";
import { Spinner } from "@/components/ui/Spinner";

/**
 * GUGiK national reference WMS overlays (cadastre + utilities), shared by every Leaflet map.
 *
 * Verified live (GetCapabilities + GetMap pixel analysis, 2026-06):
 *   KIEG  (ewidencja gruntów) → "dzialki" (granice, renders ~zoom 14+) and "numery_dzialek"
 *         (numery, ~zoom 16+). Requested together so one toggle shows boundaries AND numbers.
 *   GESUT (uzbrojenie terenu) → the geometry lives in the "przewod_*" layers. The aggregate
 *         "kgesut_dane" is a NON-rendering group layer (returns a 140 B fully-transparent tile
 *         at every scale/city) — must NOT be used. przewod_* have MaxScaleDenominator=1000,
 *         which on Leaflet's 256px EPSG:3857 tiles at PL latitude renders from ~zoom 17.
 *
 * Both services advertise EPSG:3857, so Leaflet's default Web Mercator works with no axis-order
 * trap and no proxy (tiles load cross-origin as <img>). Coverage is genuinely patchy — only
 * digitised powiats hold data and utilities sit in corridors — so a blank overlay over a given
 * spot means "no GESUT data here", not an error.
 */
const KIEG_WMS = "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow";
const GESUT_WMS = "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu";
// ORTO (ortofotomapa) → single queryable layer "Raster". Verified live (GetCapabilities, 2026-06):
// advertises EPSG:3857, so Leaflet's default Web Mercator works with no axis-order trap. Opaque
// aerial imagery with full country coverage — used as a photo basemap under the cadastre overlays.
export const ORTO_WMS = "https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/StandardResolution";
// MPZP (Miejscowe Plany Zagospodarowania Przestrzennego) — national registry via integracja.gugik.gov.pl.
// Layer order: raster scan backgrounds first, then zones/surfaces, then administrative boundaries,
// then linear/point objects on top. Same integracja infrastructure as KIEG/GESUT → EPSG:3857 works.
// integracja.gugik.gov.pl redirects (302) to mapy.geoportal.gov.pl — Leaflet tile requests
// don't follow redirects, so point directly at the canonical URL (verified via GetCapabilities).
// Layer `gminy` is absent from this endpoint (it lives on integracja, which 302s); omitted.
const MPZP_WMS =
  "https://mapy.geoportal.gov.pl/wss/ext/KrajowaIntegracjaMiejscowychPlanowZagospodarowaniaPrzestrzennego";
const MPZP_LAYERS = "raster,wektor-str,wektor-pow,granice,plany_granice,wektor-lin,wektor-lzb,wektor-pkt";
// Deep link to ONE działka in the NEW Geoportal (imapnext, live since 2026-10-05). The old
// `mapy.geoportal.gov.pl/mobile/?identifyParcel=` now 302s to imapnext and DROPS the query, so
// it lands on a 1:4 000 000 map of Poland; `identifyParcel` on the new viewer is documented by
// GUGiK (30.04.2025) and verified live 2026-10-21 — it opens the parcel at 1:500 with the
// Identyfikacja panel. The old `imap/` viewer itself is switched off on 31.12.2026.
export const GEOPORTAL_PARCEL_BASE = "https://mapy.geoportal.gov.pl/imapnext/imap/";
export function geoportalParcelUrl(teryt: string): string {
  return `${GEOPORTAL_PARCEL_BASE}?identifyParcel=${encodeURIComponent(teryt.trim())}`;
}
// GDOŚ (formy ochrony przyrody) — sdi.gdos.gov.pl. Verified live (GetCapabilities + GetMap,
// 2026-07): capabilities list EPSG:900913/3785 but the server also accepts EPSG:3857 (alias),
// so Leaflet's default Web Mercator works. No scale limits on these 5 layers — they render at
// every zoom. Order: large-area forms first, Rezerwaty last so the smallest form draws on top.
const GDOS_WMS = "https://sdi.gdos.gov.pl/wms";
const GDOS_LAYERS = [
  "GDOS:ParkiKrajobrazowe",
  "GDOS:ObszarySpecjalnejOchrony",
  "GDOS:SpecjalneObszaryOchrony",
  "GDOS:ParkiNarodowe",
  "GDOS:Rezerwaty",
].join(",");
const GESUT_LAYERS = [
  "przewod_wodociagowy",
  "przewod_kanalizacyjny",
  "przewod_gazowy",
  "przewod_elektroenergetyczny",
  "przewod_telekomunikacyjny",
  "przewod_cieplowniczy",
  "przewod_niezidentyfikowany",
  "przewod_specjalny",
  "przewod_urzadzenia",
].join(",");
// Konserwator zabytków — rejestr zabytków nieruchomych (NID, geoportal zabytek.gov.pl). INSPIRE
// view service (PS.ProtectedSites). Verified live (GetCapabilities, 2026-07): single layer
// "Immovable_Monuments", WMS 1.3.0, advertises EPSG:3857 so Leaflet's default Web Mercator works
// with no proxy. Renders at every zoom (no scale limit); transparent PNG falls back to the basemap
// outside coverage. Optional overlay — only maps that opt in (init `konserwator` in GugikState) show it.
const KONSERWATOR_WMS = "https://usluga.zabytek.gov.pl/INSPIRE_IMD/service.svc/get";
const KONSERWATOR_LAYERS = "Immovable_Monuments";
// Ewidencja zabytków nieruchomych — Krajowa Ewidencja Zabytków (NID), the broader set next to the
// register above (every register entry plus the gminna/wojewódzka records). Same NID host, plain
// (non-INSPIRE) service "ZEN". Verified live (GetCapabilities + GetMap pixel analysis, 2026-09):
// WMS 1.3.0, advertises EPSG:3857, four scale-banded layers I…IV that the SERVER switches by
// scale denominator — requested together so exactly one band draws at any zoom. The server derives
// the denominator from the request's Mercator extent / pixel width (no latitude correction), so on
// 256px tiles zoom z ↔ SD ≈ 559e6 / 2^z: band I (SD 200–10 000) covers zoom 16–21, band II zoom 15,
// bands III/IV zoom 10–14 — and those two hold only sparse generalised footprints, so real content
// starts at ~zoom 15 (GUGIK_KONSERWATOR_EWIDENCJA_MIN_ZOOM). Keep 256px tiles: 512px halves the
// denominator and pushes zoom 21 under band I's floor → blank. Fills come semi-transparent in the
// PNG (alpha ≈ 1/3). Same opt-in pattern as `konserwator`.
const KONSERWATOR_EWIDENCJA_WMS = "https://usluga.zabytek.gov.pl/ZEN/service.svc/get";
const KONSERWATOR_EWIDENCJA_LAYERS = [
  "Ewidencja_zabytkow_nieruchomych_I",
  "Ewidencja_zabytkow_nieruchomych_II",
  "Ewidencja_zabytkow_nieruchomych_III",
  "Ewidencja_zabytkow_nieruchomych_IV",
].join(",");

// GDDKiA — dane sieci dróg krajowych z KPD (Krajowy Punkt Dostępowy), the open JSON API behind
// the public "Mapa informacji drogowej" (drogi.gddkia.gov.pl). Verified live (2026-08): no auth,
// CORS *, so the browser fetches it directly like the WMS overlays above. Two feeds:
//   junctions.json — every named junction ("węzeł") on the network (~744 records, whole country,
//       ~140 KB, no parameters) → fetched once per session and kept.
//   refpoints.json — reference points ("słupki pikietażowe"); REQUIRES `extent` (EPSG:4326 bbox,
//       SEMICOLON-separated "minLon;minLat;maxLon;maxLat") and `frequency` (post spacing in
//       metres — the official map uses 100/1000/10000 by zoom) → re-fetched per map view.
// Unlike the tile overlays this is a vector layer (GddkiaDataLayer below), drawn on canvas.
const KPD_BASE = "https://kpd.gddkia.gov.pl/amp/api/waypoints/";

/** Map zoom at which each overlay starts returning non-blank tiles (server scale limits). */
export const GUGIK_PARCELS_MIN_ZOOM = 14; // granice ~14, numery ~16
export const GUGIK_INFRA_MIN_ZOOM = 17;
// granice planów od ~zoom 13, raster treść od ~zoom 16, wektory szczegółów od ~zoom 17
export const GUGIK_MPZP_MIN_ZOOM = 13;
// ewidencja zabytków: bands III/IV (zoom 10–14) are all but empty, objects draw from band II at ~15
export const GUGIK_KONSERWATOR_EWIDENCJA_MIN_ZOOM = 15;
/** Zoom from which the GDDKiA layer requests hectometre (100 m) posts. */
export const GDDKIA_HECTOMETRE_ZOOM = 15;

export interface GugikLayers {
  parcels: L.TileLayer.WMS;
  infra: L.TileLayer.WMS;
  ortho: L.TileLayer.WMS;
  mpzp: L.TileLayer.WMS;
  gdos: L.TileLayer.WMS;
  konserwator: L.TileLayer.WMS;
  konserwatorEwidencja: L.TileLayer.WMS;
  gddkia: GddkiaDataLayer;
  closedAreas: ClosedAreasLayer;
}

export interface GugikState {
  parcels: boolean;
  infra: boolean;
  ortho: boolean;
  mpzp: boolean;
  gdos: boolean;
  // Optional: only present on maps that opt into the heritage-register overlay (MapTab, megamap,
  // ZE editor).
  // Undefined elsewhere, which hides its checkbox in GugikLayerPanel.
  konserwator?: boolean;
  // Optional: ewidencja zabytków (the broader NID records set), same opt-in as `konserwator`.
  konserwatorEwidencja?: boolean;
  // Optional: GDDKiA road-network data (węzły + słupki pikietażowe), same opt-in pattern.
  gddkia?: boolean;
  // Optional: tereny zamknięte MON (our own layer built from the MON decision), same opt-in.
  closedAreas?: boolean;
}

interface GddkiaJunction {
  id: string;
  branch: string;
  name: string;
  road_number: string;
  mileage: number;
  number: string | null;
  latitude: number;
  longitude: number;
}

interface GddkiaRefPoint {
  id: number;
  road_number: string;
  mileage: number;
  latitude: number;
  longitude: number;
}

/** 398.5 → "398+500" — the kilometrage notation road engineers expect on a słupek. */
function formatMileage(km: number): string {
  const whole = Math.floor(km);
  const metres = Math.round((km - whole) * 1000);
  return `${whole}+${String(metres).padStart(3, "0")}`;
}

/** Post spacing (m) requested from refpoints.json — mirrors the official map's own
 *  resolution thresholds (<3 m/px → 100 m, <80 m/px → 1 km, else 10 km), translated
 *  to Leaflet zoom at Polish latitudes. */
export function gddkiaFrequencyForZoom(zoom: number): number {
  if (zoom >= GDDKIA_HECTOMETRE_ZOOM) return 100;
  if (zoom >= 11) return 1000;
  return 10000;
}

/** Whether a słupek gets a permanent kilometre label beside its dot. Density-limited so
 *  neighbouring labels keep ~100+ px between them (post spacing = freq, px/m = zoom): the
 *  10 km tier can label every post, the 1 km tier thins to even kilometres at zoom 11, and
 *  the 100 m tier steps 500 m → 200 m → every post as the zoom closes in — a project frame
 *  at zoom 16+ spans a few hundred metres, so a whole-km-only rule would usually label
 *  nothing there. Below zoom 9 (country overview) the labels would overlap AND cost
 *  thousands of DOM nodes, so they drop to hover-only. */
function kmLabelVisible(mileage: number, zoom: number, freq: number): boolean {
  if (zoom < 9) return false;
  if (freq === 10000) return true;
  if (freq === 1000) return zoom >= 12 || Math.round(mileage * 1000) % 2000 === 0;
  const metres = Math.round(mileage * 1000);
  if (zoom >= 17) return true;
  if (zoom >= 16) return metres % 200 === 0;
  return metres % 500 === 0;
}

/** Real słupki kilometrowe carry just the kilometre number — mirror that ("398"); the rare
 *  non-integer post (a 10 km-tier road ending mid-kilometre) falls back to "3+400". */
function kmLabelText(mileage: number): string {
  return Math.round(mileage * 1000) % 1000 === 0
    ? String(Math.round(mileage))
    : formatMileage(mileage);
}

// Inline-styled (no stylesheet dependency) kilometre label: monospace with a white halo so it
// reads over both OSM and the ortho, non-interactive so it never steals the dot's tooltip/clicks.
// Navy (matching the dots) — distinct from both the OSM road ribbons and the violet junctions.
const KM_LABEL_STYLE =
  "font:600 10px/1 ui-monospace,SFMono-Regular,monospace;color:#1e3a8a;white-space:nowrap;" +
  "text-shadow:0 1px 2px #fff,0 -1px 2px #fff,1px 0 2px #fff,-1px 0 2px #fff;pointer-events:none;";

/**
 * Vector overlay with GDDKiA road-network data: junctions ("węzły", violet, fetched once —
 * the set changes a few times a year) and reference points ("słupki pikietażowe", navy,
 * re-fetched on every moveend for the padded view bbox at the zoom-appropriate density).
 * Both draw as canvas circle markers with hover tooltips ("Węzeł Brzęczkowice • A4 km
 * 340+500" / "S1 km 398+500"); from ~zoom 9 the słupki additionally carry a permanent
 * kilometre label (see kmLabelVisible). Exposes the same `_loading` + "loading"/"load"
 * contract as the WMS overlays so useGugikLoading counts its fetches without special-casing.
 *
 * KPD has no SLA (it is the public map's internal API): every fetch failure is silent —
 * the layer simply stays/goes empty, like a WMS overlay outside its coverage.
 */
export class GddkiaDataLayer extends L.LayerGroup {
  _loading?: boolean;
  _gugikRetrying?: number; // structural parity with RetryLayer for useGugikLoading's busy()
  private map: L.Map | null = null;
  private junctionsRenderer: L.Canvas;
  private refpointsRenderer: L.Canvas;
  private junctionsGroup = L.layerGroup();
  private refpointsGroup = L.layerGroup();
  private junctionsLoaded = false;
  private abort?: AbortController;
  private debounce?: ReturnType<typeof setTimeout>;
  private inflight = 0;

  private refpointsPane: string;

  constructor(refpointsPane: string, junctionsPane: string) {
    super();
    this.refpointsPane = refpointsPane; // km-label markers share the dots' pane
    // One canvas renderer per pane: cheap for thousands of points, and the draw order
    // (słupki below, węzły above) is fixed by the panes, not by insertion order.
    this.refpointsRenderer = L.canvas({ pane: refpointsPane });
    this.junctionsRenderer = L.canvas({ pane: junctionsPane });
    this.addLayer(this.refpointsGroup);
    this.addLayer(this.junctionsGroup);
  }

  onAdd(map: L.Map): this {
    super.onAdd(map);
    this.map = map;
    map.on("moveend", this.scheduleRefresh);
    void this.loadJunctions();
    this.refreshRefpoints();
    return this;
  }

  onRemove(map: L.Map): this {
    map.off("moveend", this.scheduleRefresh);
    clearTimeout(this.debounce);
    this.abort?.abort();
    this.map = null;
    super.onRemove(map);
    return this;
  }

  /** Debounced so a pan that settles in steps fires one fetch, not five. Arrow field —
   *  stable identity for map.off(). */
  private scheduleRefresh = () => {
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.refreshRefpoints(), 300);
  };

  private begin() {
    if (++this.inflight === 1) {
      this._loading = true;
      this.fire("loading");
    }
  }

  private end() {
    if (--this.inflight === 0) {
      this._loading = false;
      this.fire("load");
    }
  }

  private async loadJunctions(): Promise<void> {
    if (this.junctionsLoaded) return;
    this.junctionsLoaded = true; // guard against re-entry; reset below on failure
    this.begin();
    try {
      const res = await fetch(`${KPD_BASE}junctions.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { response: GddkiaJunction[] };
      for (const j of body.response) {
        L.circleMarker([j.latitude, j.longitude], {
          renderer: this.junctionsRenderer,
          radius: 5,
          color: "#ffffff",
          weight: 1.5,
          fillColor: "#7c3aed",
          fillOpacity: 1,
        })
          .bindTooltip(`Węzeł ${j.name} • ${j.road_number} km ${formatMileage(j.mileage)}`, {
            direction: "top",
          })
          .addTo(this.junctionsGroup);
      }
    } catch {
      this.junctionsLoaded = false; // next toggle-on retries
    } finally {
      this.end();
    }
  }

  private refreshRefpoints(): void {
    const map = this.map;
    if (!map) return;
    this.abort?.abort(); // a newer view supersedes the in-flight fetch
    const ctrl = (this.abort = new AbortController());
    const freq = gddkiaFrequencyForZoom(map.getZoom());
    // Pad the bbox so a small pan reveals already-drawn posts instead of blank edge.
    const b = map.getBounds().pad(0.2);
    const extent = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
      .map((v) => v.toFixed(4))
      .join(";");
    this.begin();
    fetch(`${KPD_BASE}refpoints.json?extent=${extent}&frequency=${freq}`, { signal: ctrl.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ response: GddkiaRefPoint[] }>;
      })
      .then((body) => {
        if (ctrl.signal.aborted) return;
        this.refpointsGroup.clearLayers();
        const zoom = map.getZoom();
        for (const p of body.response) {
          L.circleMarker([p.latitude, p.longitude], {
            renderer: this.refpointsRenderer,
            radius: 2.5,
            stroke: false,
            fillColor: "#1e3a8a",
            fillOpacity: 0.9,
          })
            .bindTooltip(`${p.road_number} km ${formatMileage(p.mileage)}`, { direction: "top" })
            .addTo(this.refpointsGroup);
          if (kmLabelVisible(p.mileage, zoom, freq)) {
            L.marker([p.latitude, p.longitude], {
              pane: this.refpointsPane,
              interactive: false,
              keyboard: false,
              icon: L.divIcon({
                className: "", // suppress .leaflet-div-icon's white box + border
                iconSize: [0, 0],
                iconAnchor: [-4, 7], // top-left of the label 4 px right of the dot, v-centred
                html: `<span style="${KM_LABEL_STYLE}">${kmLabelText(p.mileage)}</span>`,
              }),
            }).addTo(this.refpointsGroup);
          }
        }
      })
      .catch(() => {
        // Aborted (superseded view) or KPD hiccup — keep whatever is drawn.
      })
      .finally(() => this.end());
  }
}

// ── Tereny zamknięte MON ──────────────────────────────────────────────────────────────
// No public WMS publishes them: the MON decision ("w sprawie ustalenia terenów zamkniętych",
// Dz. Urz. MON) only lists cadastral parcels. The backend turns that list into a static
// GeoJSON (`manage.py build_mon_closed_areas`, one feature per decision item — "lp." — plus a
// separate `partial` feature for parcels the decision closes only "w części") and serves it
// from GET /closed-areas/. The file is refreshed by hand after each new decision, so the
// layer label carries the date of the last refresh (GET /closed-areas/meta/).

export interface ClosedAreasDecision {
  number: string;
  issued: string | null;
  published: string | null;
  position: string;
  amendment: boolean;
  url: string;
}

export interface ClosedAreasMeta {
  updated_at?: string;
  decisions?: ClosedAreasDecision[];
  count: number;
}

export interface ClosedAreaProps {
  lp: number;
  partial: boolean;
  decision: string;
  localities: string[];
  counties: string[];
  voivodeships: string[];
  obreby: string[];
  storage: string;
  parcels: number;
  found: number;
  missing: number;
  area_ha: number;
}

type ClosedAreaGeometry =
  | { type: "Polygon"; coordinates: number[][][] }
  | { type: "MultiPolygon"; coordinates: number[][][][] };

interface ClosedAreaFeature {
  type: "Feature";
  properties: ClosedAreaProps;
  geometry: ClosedAreaGeometry;
}

interface ClosedAreaItem {
  bounds: L.LatLngBounds;
  feature: ClosedAreaFeature;
  path: L.Path;
}

// One request per page load, shared by every panel/layer instance (the file changes a few times
// a year). A failed request is forgotten so the next mount retries.
let closedAreasMetaPromise: Promise<ClosedAreasMeta | null> | null = null;

export function loadClosedAreasMeta(): Promise<ClosedAreasMeta | null> {
  if (!closedAreasMetaPromise) {
    closedAreasMetaPromise = apiClient
      .get<{ data: ClosedAreasMeta }>("/closed-areas/meta/")
      .then((r) => r.data.data)
      .catch(() => {
        closedAreasMetaPromise = null;
        return null;
      });
  }
  return closedAreasMetaPromise;
}

/** Layer metadata for the panel label; null until loaded (or when `enabled` is false). */
export function useClosedAreasMeta(enabled: boolean): ClosedAreasMeta | null {
  const [meta, setMeta] = useState<ClosedAreasMeta | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void loadClosedAreasMeta().then((m) => {
      if (alive) setMeta(m);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return meta;
}

/** "2026-10-14" → "14.10.2026". */
export function formatIsoDatePl(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

/** "Decyzja 58/MON z 14.05.2026 ze zm. (128/MON)" — the legal basis of the current file. */
export function closedAreasBasis(meta: ClosedAreasMeta | null): string {
  const decisions = meta?.decisions ?? [];
  const base = decisions.find((d) => !d.amendment);
  if (!base) return "";
  const amendments = decisions.filter((d) => d.amendment).map((d) => d.number);
  return (
    `Decyzja ${base.number} z ${formatIsoDatePl(base.issued)}` +
    (amendments.length ? ` ze zm. (${amendments.join(", ")})` : "")
  );
}

/** Even-odd point-in-polygon over every ring (holes included) of a GeoJSON (Multi)Polygon. */
export function pointInClosedArea(lng: number, lat: number, geometry: ClosedAreaGeometry): boolean {
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  let inside = false;
  for (const rings of polygons) {
    for (const ring of rings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i];
        const [xj, yj] = ring[j];
        if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
      }
    }
  }
  return inside;
}

const escHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/** Hover card of one decision item. Long poligony list dozens of obręby — capped at 4. */
export function closedAreaTooltipHtml(p: ClosedAreaProps): string {
  const place = [p.localities.join(", "), p.counties.map((c) => `pow. ${c.toLowerCase()}`).join(", ")]
    .filter(Boolean)
    .join(" · ");
  const obreby = p.obreby.slice(0, 4).join("; ") + (p.obreby.length > 4 ? `; … (+${p.obreby.length - 4})` : "");
  const lines = [
    `<b>Teren zamknięty MON — lp. ${p.lp}</b>`,
    place && escHtml(place),
    obreby && `Obręby: ${escHtml(obreby)}`,
    p.decision && `Decyzja ${escHtml(p.decision)}`,
    p.storage && `Dokumentacja: ${escHtml(p.storage)}`,
    p.partial &&
      `<span style="color:#b45309">Część działki — decyzja nie podaje, która; zaznaczona cała działka.</span>`,
    p.missing > 0 &&
      `<span style="color:#b45309">Nie odnaleziono w ULDK ${p.missing} z ${p.parcels} dz. tej lp.</span>`,
  ];
  // width:max-content — the tooltip pane is 0 px wide, so a plain wrapping box would shrink to
  // its longest word.
  return `<div style="width:max-content;max-width:300px;white-space:normal;line-height:1.35">${lines.filter(Boolean).join("<br>")}</div>`;
}

const CLOSED_AREA_STYLE: L.PathOptions = {
  color: "#b91c1c",
  weight: 1.5,
  fillColor: "#ef4444",
  fillOpacity: 0.15,
  interactive: false,
};
const CLOSED_AREA_PARTIAL_STYLE: L.PathOptions = {
  ...CLOSED_AREA_STYLE,
  dashArray: "4 4",
  fillOpacity: 0.06,
};

/**
 * Vector overlay with the closed areas (GeoJSON from our API, fetched once per page — the
 * `?v=<updated_at>` query busts the day-long browser cache after a refresh). Polygons are drawn
 * NON-interactive and the hover card is driven from the map's own `mousemove` with a manual
 * hit test: the maps keep their interactive vectors in higher SVG panes, whose full-size <svg>
 * root swallows pointer events before they could reach a path in this lower pane — and a
 * clickable layer would also steal clicks from the map tools. Same `_loading` contract as the
 * GDDKiA layer so useGugikLoading counts the fetch.
 */
export class ClosedAreasLayer extends L.LayerGroup {
  _loading?: boolean;
  _gugikRetrying?: number;
  private map: L.Map | null = null;
  private pane: string;
  private items: ClosedAreaItem[] = [];
  private loaded = false;
  private hovered: L.Path | null = null;
  // "auto" opens beside the cursor, away from the map centre — clear of the layer panel.
  private tip = L.tooltip({ direction: "auto", offset: [12, 0], opacity: 0.95 });
  private frame = 0;

  constructor(pane: string) {
    super();
    this.pane = pane;
  }

  onAdd(map: L.Map): this {
    super.onAdd(map);
    this.map = map;
    map.on("mousemove", this.onMove);
    map.on("mouseout", this.clearHover);
    void this.load();
    return this;
  }

  onRemove(map: L.Map): this {
    map.off("mousemove", this.onMove);
    map.off("mouseout", this.clearHover);
    cancelAnimationFrame(this.frame);
    this.clearHover();
    this.map = null;
    super.onRemove(map);
    return this;
  }

  private async load(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true; // guard against re-entry; reset below on failure
    this._loading = true;
    this.fire("loading");
    try {
      const meta = await loadClosedAreasMeta();
      const res = await apiClient.get<{ data: { features: ClosedAreaFeature[] } }>("/closed-areas/", {
        params: { v: meta?.updated_at ?? "" },
      });
      for (const feature of res.data.data.features) {
        const path = L.geoJSON(feature as GeoJSON.Feature, {
          pane: this.pane,
          style: feature.properties.partial ? CLOSED_AREA_PARTIAL_STYLE : CLOSED_AREA_STYLE,
        });
        path.addTo(this);
        this.items.push({ bounds: path.getBounds(), feature, path: path.getLayers()[0] as L.Path });
      }
    } catch {
      this.loaded = false; // next toggle-on retries
    } finally {
      this._loading = false;
      this.fire("load");
    }
  }

  /** Arrow field — stable identity for map.off(). One hit test per animation frame. */
  private onMove = (e: L.LeafletMouseEvent) => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.hitTest(e.latlng));
  };

  private hitTest(latlng: L.LatLng) {
    const map = this.map;
    if (!map) return;
    let best: ClosedAreaItem | null = null;
    for (const item of this.items) {
      if (!item.bounds.contains(latlng)) continue;
      if (!pointInClosedArea(latlng.lng, latlng.lat, item.feature.geometry)) continue;
      if (!best || item.feature.properties.area_ha < best.feature.properties.area_ha) best = item;
    }
    if (!best) {
      this.clearHover();
      return;
    }
    const path = best.path;
    if (this.hovered !== path) {
      this.clearHover();
      this.hovered = path;
      path.setStyle({ weight: 3 });
      this.tip.setContent(closedAreaTooltipHtml(best.feature.properties));
    }
    this.tip.setLatLng(latlng);
    if (!map.hasLayer(this.tip)) this.tip.addTo(map);
  }

  private clearHover = () => {
    if (this.hovered) {
      this.hovered.setStyle({ weight: CLOSED_AREA_STYLE.weight });
      this.hovered = null;
    }
    this.map?.removeLayer(this.tip);
  };
}

/** A GUGiK tile layer carrying Leaflet's batch-loading flag plus our own count of tiles still
 *  cycling through retries — the two together tell the spinner whether work remains (see below). */
type RetryLayer = L.TileLayer & { _loading?: boolean; _gugikRetrying?: number };

/** What useGugikLoading actually needs from a layer: the busy flags plus Leaflet's event bus.
 *  Both the WMS tile overlays (RetryLayer) and the GDDKiA vector layer satisfy it. */
type BusyLayer = L.Evented & { _loading?: boolean; _gugikRetrying?: number };

/**
 * GUGiK's ortofoto WMS renders every tile on demand and, under load, drops a large random
 * fraction of GetMap requests with a 404 instead of an image (verified live: ~half of even
 * sequential requests 404, varying run to run). Leaflet treats that as a dead tile and leaves it
 * blank — most visible as the basemap bleeding through the OPAQUE ortofoto, producing the patchy
 * checkerboard. Re-fetch each errored tile up to `max` times with a growing backoff: each attempt
 * is an independent ~50/50, so 10 spaced retries drive the miss chance to ~0.1%. The cache-buster
 * stops the 404 being replayed from cache, the `parentNode` guard skips tiles Leaflet already
 * pruned (panned away), and the cap stops a genuinely-absent tile from looping forever.
 *
 * `_gugikRetrying` counts tiles currently in a retry cycle (backoff wait + in-flight refetch) so
 * useGugikLoading can keep the spinner up while gaps are still filling — Leaflet's own `load`
 * fires the moment every tile has loaded OR errored, i.e. before any retry has run. It is bumped
 * when a retry is scheduled and released exactly once per attempt when that attempt resolves; a
 * failed attempt re-fires `tileerror`, which bumps again for the next attempt, so the count holds
 * above zero across the whole chain and only drains when every tile has succeeded or hit the cap.
 */
function attachTileRetry(layer: L.TileLayer, max = 10) {
  const lyr = layer as RetryLayer;
  lyr._gugikRetrying = 0;
  const bump = (delta: number) => {
    lyr._gugikRetrying = Math.max(0, (lyr._gugikRetrying ?? 0) + delta);
    layer.fire("gugikretrychange");
  };
  layer.on("tileerror", (e: L.TileErrorEvent) => {
    const tile = e.tile as HTMLImageElement & { _gugikRetry?: number };
    const n = (tile._gugikRetry = (tile._gugikRetry ?? 0) + 1);
    if (n > max) return;
    bump(+1); // hold "busy" across the backoff wait, not just the in-flight request
    const baseSrc = tile.src.split("&_r=")[0]; // WMS URL always has a query string already
    setTimeout(() => {
      if (!tile.parentNode) {
        bump(-1); // pruned while waiting — abandon this attempt
        return;
      }
      // Release this attempt once it resolves. Leaflet's own (persistent) load/error handlers
      // run first: on failure they re-fire `tileerror`, which bumps for the NEXT attempt before
      // this settle bumps down — so the count never dips to zero mid-chain (no spinner flicker).
      // The watchdog covers a refetch whose socket hangs (neither load nor error ever fires, a
      // real failure for the same overloaded WMS): without it `_gugikRetrying` would stay >0 for
      // good and light a phantom spinner on a later, unrelated overlay toggle.
      let watchdog: ReturnType<typeof setTimeout>;
      const settle = () => {
        clearTimeout(watchdog);
        tile.removeEventListener("load", settle);
        tile.removeEventListener("error", settle);
        bump(-1);
      };
      tile.addEventListener("load", settle);
      tile.addEventListener("error", settle);
      watchdog = setTimeout(settle, 15000);
      tile.src = `${baseSrc}&_r=${n}`;
    }, 400 * n);
  });
}

/**
 * Create the GUGiK overlay layers in dedicated panes on `map` (call once at map init). Panes
 * sit above OSM (200) and below `baseZ + 20`; pass `baseZ` so they slot under any custom panes
 * a map already owns (e.g. the ZE editor's local-parcels pane at 350 — baseZ 300 keeps GUGiK
 * below it). Layers carry `minZoom` so they never fire blank requests below their scale limit.
 */
export function createGugikLayers(map: L.Map, baseZ = 300): GugikLayers {
  // Ortho sits one pane BELOW the cadastre/utility overlays (and any local parcels pane a map
  // owns, e.g. the ZE editor's at 350) so parcel borders/numbers + GESUT stay legible on top of
  // the aerial imagery; still above OSM (200), which it covers when enabled (opaque photo basemap).
  map.createPane("gugikOrtho").style.zIndex = String(baseZ - 10);
  map.createPane("gugikMpzp").style.zIndex = String(baseZ - 5);
  map.createPane("gugikGdos").style.zIndex = String(baseZ - 4);
  // Konserwator (zabytki) sits just above GDOŚ but below the cadastre/utility overlays, so parcel
  // borders and numbers stay legible on top of the heritage points/areas. Ewidencja (the broader
  // set, mostly area fills) goes UNDER the rejestr so the stricter register marks always show.
  map.createPane("gugikKonserwatorEwidencja").style.zIndex = String(baseZ - 3);
  map.createPane("gugikKonserwator").style.zIndex = String(baseZ - 2);
  map.createPane("gugikInfra").style.zIndex = String(baseZ);
  // Tereny zamknięte: above the thematic overlays, below the cadastre so parcel borders and
  // numbers stay readable through the translucent fill.
  map.createPane("monClosedAreas").style.zIndex = String(baseZ + 5);
  map.createPane("gugikParcels").style.zIndex = String(baseZ + 10);
  // GDDKiA vector data sits on top of every tile overlay: point markers must stay clickable
  // above the opaque ortho and the parcel borders. Słupki below węzły (separate panes) so a
  // junction dot is never buried under a cluster of hectometre posts.
  map.createPane("gddkiaRefpoints").style.zIndex = String(baseZ + 14);
  map.createPane("gddkiaJunctions").style.zIndex = String(baseZ + 15);
  const base = { format: "image/png", transparent: true, version: "1.3.0", maxZoom: 21 } as const;
  // Full-country pyramid — renders at every zoom, so no minZoom (unlike the scale-limited
  // cadastre/GESUT layers). 512px tiles quarter the request count vs 256, so fewer hit the
  // service's per-burst 404 load-shedding (parcels/infra stay 256 — their render-from-zoom
  // thresholds were tuned at that tile size). Transparent PNG falls back to the basemap
  // outside coverage.
  const ortho = L.tileLayer.wms(ORTO_WMS, {
    ...base,
    layers: "Raster",
    pane: "gugikOrtho",
    tileSize: 512,
  });
  const mpzp = L.tileLayer.wms(MPZP_WMS, {
    ...base,
    layers: MPZP_LAYERS,
    pane: "gugikMpzp",
    minZoom: GUGIK_MPZP_MIN_ZOOM,
    opacity: 0.8,
  });
  // Semi-transparent so the basemap/ortho stays readable under the large colored areas.
  const gdos = L.tileLayer.wms(GDOS_WMS, {
    ...base,
    layers: GDOS_LAYERS,
    pane: "gugikGdos",
    opacity: 0.6,
  });
  // Rejestr zabytków nieruchomych (NID). Slightly higher opacity than GDOŚ — these are points /
  // small footprints rather than large fills, so they need to read clearly over the basemap.
  // `crossOrigin` on both NID layers: the services answer GetMap with Access-Control-Allow-Origin
  // `*` (verified 2026-09), and an anonymous-CORS <img> is the only way useNidTileContent can read
  // the pixels back to tell "no monuments here" from "layer broken".
  const konserwator = L.tileLayer.wms(KONSERWATOR_WMS, {
    ...base,
    layers: KONSERWATOR_LAYERS,
    pane: "gugikKonserwator",
    opacity: 0.8,
    crossOrigin: "anonymous",
  });
  // Ewidencja zabytków nieruchomych (NID). minZoom skips the near-empty coarse bands; default
  // 256px tiles on purpose (see the KONSERWATOR_EWIDENCJA_* note on the scale bands).
  const konserwatorEwidencja = L.tileLayer.wms(KONSERWATOR_EWIDENCJA_WMS, {
    ...base,
    layers: KONSERWATOR_EWIDENCJA_LAYERS,
    pane: "gugikKonserwatorEwidencja",
    minZoom: GUGIK_KONSERWATOR_EWIDENCJA_MIN_ZOOM,
    opacity: 0.8,
    crossOrigin: "anonymous",
  });
  const infra = L.tileLayer.wms(GESUT_WMS, {
    ...base,
    layers: GESUT_LAYERS,
    pane: "gugikInfra",
    minZoom: GUGIK_INFRA_MIN_ZOOM,
  });
  const parcels = L.tileLayer.wms(KIEG_WMS, {
    ...base,
    layers: "dzialki,numery_dzialek",
    pane: "gugikParcels",
    minZoom: GUGIK_PARCELS_MIN_ZOOM,
  });
  const gddkia = new GddkiaDataLayer("gddkiaRefpoints", "gddkiaJunctions");
  const closedAreas = new ClosedAreasLayer("monClosedAreas");
  // Recover the tiles GUGiK drops with a 404 under concurrent load (see attachTileRetry).
  // (gddkia and closedAreas are vector JSON, not tiles — they manage their own fetch lifecycle.)
  for (const layer of [ortho, mpzp, gdos, konserwatorEwidencja, konserwator, infra, parcels]) {
    attachTileRetry(layer);
  }
  return { ortho, mpzp, gdos, konserwator, konserwatorEwidencja, infra, parcels, gddkia, closedAreas };
}

const GUGIK_STATE_STORAGE_PREFIX = "gugik.layers.";

/**
 * GugikState that survives a remount. Every map here lives inside a tab: switching to another
 * tab and back unmounts the map, which would reset the checkboxes and force the user to
 * re-click działki / GESUT / ortofoto every time. Persisting the choice per browser (like the
 * panel's own collapsed flag) makes the overlays stay where the user left them.
 *
 * `scope` separates maps that mean different things (project map vs megamap vs formatka), so
 * turning the ortofoto on in one doesn't silently change another. Only keys present in
 * `initial` are restored — a map that doesn't opt into `konserwator` never gains the checkbox
 * from stale storage, and a key added later starts from its new default rather than `false`.
 */
export function useGugikState(
  scope: string,
  initial: GugikState,
): [GugikState, (next: GugikState) => void] {
  const key = GUGIK_STATE_STORAGE_PREFIX + scope;
  // Defaults only matter on first mount; freeze them so an inline object literal (new identity
  // every render) can't retrigger anything.
  const initialRef = useRef(initial);
  const [gugik, setGugik] = useState<GugikState>(() => {
    const base = initialRef.current;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return base;
      const saved = JSON.parse(raw) as Record<string, unknown>;
      const next = { ...base };
      for (const k of Object.keys(base) as (keyof GugikState)[]) {
        if (typeof saved[k] === "boolean") next[k] = saved[k] as boolean;
      }
      return next;
    } catch {
      // Malformed JSON or storage unavailable (private mode) — fall back to the defaults.
      return base;
    }
  });
  const update = useCallback(
    (next: GugikState) => {
      setGugik(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Ignore write failures (quota / private mode) — persistence is best-effort.
      }
    },
    [key],
  );
  return [gugik, update];
}

/** Toggle a GUGiK overlay on/off via addLayer/removeLayer (a hidden layer stops fetching tiles).
 *  Generic so the same hook drives WMS tile overlays and the GDDKiA vector layer. */
export function useGugikToggle<T extends L.Layer>(
  mapRef: MutableRefObject<L.Map | null>,
  layerRef: MutableRefObject<T | null>,
  on: boolean,
) {
  useEffect(() => {
    const map = mapRef.current;
    const lyr = layerRef.current;
    if (!map || !lyr) return;
    if (on) {
      if (!map.hasLayer(lyr)) lyr.addTo(map);
    } else if (map.hasLayer(lyr)) {
      map.removeLayer(lyr);
    }
  }, [mapRef, layerRef, on]);
}

/**
 * Does a loaded NID tile carry any drawing? The services answer "nothing here" with a fully
 * transparent 384-byte PNG, indistinguishable from content in the DOM — so the pixels are read
 * back through a small canvas (alpha only; downscaled 8× to keep it ~1k px per tile). Returns
 * null when the pixels cannot be read (tainted canvas, no 2D context in tests) — "unknown", never
 * counted as empty. Exported for tests.
 */
export function tileHasContent(tile: HTMLImageElement): boolean | null {
  try {
    if (!tile.naturalWidth) return null;
    const size = 32;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(tile, 0, 0, size, size);
    const px = ctx.getImageData(0, 0, size, size).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] > 0) return true;
    return false;
  } catch {
    return null;
  }
}

/**
 * "Every NID tile in view came back blank" — true only once at least one tile of the layer has
 * loaded AND none of the currently kept tiles had a single opaque pixel. The register/ewidencja
 * coverage is genuinely partial (whole rural gminas hold no digitised records), so an overlay that
 * is on yet draws nothing needs to say so, or it reads as broken. Tracks Leaflet's tile lifecycle
 * (`tileload` / `tileunload`) so panning re-evaluates; below the layer's minZoom no tiles load and
 * the answer is false.
 */
export function useNidTileContent(
  layerRef: MutableRefObject<L.TileLayer | null>,
  on: boolean,
  analyze: (tile: HTMLImageElement) => boolean | null = tileHasContent,
): boolean {
  const [empty, setEmpty] = useState(false);
  // Latest analyzer without re-arming (and so resetting) the tile bookkeeping on every render.
  const analyzeRef = useRef(analyze);
  analyzeRef.current = analyze;
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !on) {
      setEmpty(false);
      return;
    }
    const seen = new Map<string, boolean | null>();
    const recompute = () => {
      const known = [...seen.values()].filter((v): v is boolean => v !== null);
      setEmpty(known.length > 0 && known.every((v) => !v));
    };
    const key = (c: L.Coords) => `${c.z}:${c.x}:${c.y}`;
    const onLoad = (e: L.TileEvent) => {
      seen.set(key(e.coords), analyzeRef.current(e.tile as HTMLImageElement));
      recompute();
    };
    const onUnload = (e: L.TileEvent) => {
      seen.delete(key(e.coords));
      recompute();
    };
    layer.on("tileload", onLoad);
    layer.on("tileunload", onUnload);
    return () => {
      layer.off("tileload", onLoad);
      layer.off("tileunload", onUnload);
      setEmpty(false);
    };
  }, [layerRef, on]);
  return empty;
}

/**
 * True while any *enabled* GUGiK overlay still has work in flight, so the map can show our
 * spinner until the (often slow / rate-limited) GUGiK service responds. A layer is "busy" when
 * Leaflet is fetching a batch (`_loading`) OR one of its tiles is still mid-retry
 * (`_gugikRetrying`, see attachTileRetry). The retry term is the point: Leaflet fires `load` the
 * instant every tile has loaded OR errored — i.e. before any 404 retry has run — so on `_loading`
 * alone the spinner would clear while the background retries are still filling the gaps.
 *
 * Recompute on every tile event (incl. our custom `gugikretrychange`) rather than tracking a
 * delta, so the spinner always reflects authoritative state. The safety timeout re-arms on each
 * event and so fires only after a real lull — never spinning forever if the service drops a
 * response and no further event arrives. Below a layer's minZoom no tiles load → never busy.
 */
export function useGugikLoading(
  parcelsRef: MutableRefObject<L.TileLayer.WMS | null>,
  infraRef: MutableRefObject<L.TileLayer.WMS | null>,
  orthoRef: MutableRefObject<L.TileLayer.WMS | null>,
  gugik: GugikState,
  mpzpRef?: MutableRefObject<L.TileLayer.WMS | null>,
  gdosRef?: MutableRefObject<L.TileLayer.WMS | null>,
  konserwatorRef?: MutableRefObject<L.TileLayer.WMS | null>,
  konserwatorEwidencjaRef?: MutableRefObject<L.TileLayer.WMS | null>,
  gddkiaRef?: MutableRefObject<GddkiaDataLayer | null>,
  closedAreasRef?: MutableRefObject<ClosedAreasLayer | null>,
): boolean {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const active: BusyLayer[] = [];
    if (gugik.parcels && parcelsRef.current) active.push(parcelsRef.current as RetryLayer);
    if (gugik.infra && infraRef.current) active.push(infraRef.current as RetryLayer);
    if (gugik.ortho && orthoRef.current) active.push(orthoRef.current as RetryLayer);
    if (gugik.mpzp && mpzpRef?.current) active.push(mpzpRef.current as RetryLayer);
    if (gugik.gdos && gdosRef?.current) active.push(gdosRef.current as RetryLayer);
    if (gugik.konserwator && konserwatorRef?.current)
      active.push(konserwatorRef.current as RetryLayer);
    if (gugik.konserwatorEwidencja && konserwatorEwidencjaRef?.current)
      active.push(konserwatorEwidencjaRef.current as RetryLayer);
    if (gugik.gddkia && gddkiaRef?.current) active.push(gddkiaRef.current);
    if (gugik.closedAreas && closedAreasRef?.current) active.push(closedAreasRef.current);
    if (!active.length) {
      setLoading(false);
      return;
    }
    let safety: ReturnType<typeof setTimeout> | undefined;
    const busy = () => active.some((l) => l._loading || (l._gugikRetrying ?? 0) > 0);
    const refresh = () => {
      const b = busy();
      setLoading(b);
      clearTimeout(safety);
      if (b) safety = setTimeout(() => setLoading(false), 30000);
    };
    const EVENTS = ["loading", "load", "tileerror", "gugikretrychange"];
    for (const lyr of active) for (const ev of EVENTS) lyr.on(ev, refresh);
    refresh(); // catch a load already underway when the layer was toggled on
    return () => {
      clearTimeout(safety);
      for (const lyr of active) for (const ev of EVENTS) lyr.off(ev, refresh);
      setLoading(false);
    };
  }, [parcelsRef, infraRef, orthoRef, mpzpRef, gdosRef, konserwatorRef, konserwatorEwidencjaRef, gddkiaRef, closedAreasRef, gugik.parcels, gugik.infra, gugik.ortho, gugik.mpzp, gugik.gdos, gugik.konserwator, gugik.konserwatorEwidencja, gugik.gddkia, gugik.closedAreas]);
  return loading;
}

/** On-map toggle panel: one checkbox for działki (granice + numery), one for GESUT, one for
 *  the ortofotomapa (aerial basemap), with zoom / coverage hints. Collapses to a small square
 *  button (Layers icon) via the header's
 *  close button; click the square to expand again. `loading` shows our square spinner next to
 *  the title (or in the collapsed square) while the enabled overlays fetch tiles. `className`
 *  positions it (e.g. "absolute top-[78px] left-2"). */
export function GugikLayerPanel({
  gugik,
  onChange,
  zoom,
  loading,
  className,
  identifyHint,
  nidEmpty,
}: {
  gugik: GugikState;
  onChange: (next: GugikState) => void;
  zoom: number;
  loading?: boolean;
  className?: string;
  /** Shown under the NID checkboxes when either is on — tells which map tool pulls the
   *  object data from zabytek.gov.pl (only maps that wire `fetchNidMonuments` pass it). */
  identifyHint?: string;
  /** Per NID overlay: every tile in view came back blank (see useNidTileContent). */
  nidEmpty?: { rejestr?: boolean; ewidencja?: boolean };
}) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("gugik_panel_collapsed") === "1",
  );

  const toggle = (next: boolean) => {
    setCollapsed(next);
    localStorage.setItem("gugik_panel_collapsed", next ? "1" : "0");
  };

  const closedMeta = useClosedAreasMeta(gugik.closedAreas !== undefined);
  const closedUpdated = formatIsoDatePl(closedMeta?.updated_at);

  const anyOn = !!(
    gugik.parcels ||
    gugik.infra ||
    gugik.ortho ||
    gugik.mpzp ||
    gugik.gdos ||
    gugik.konserwator ||
    gugik.konserwatorEwidencja ||
    gugik.gddkia ||
    gugik.closedAreas
  );
  // Clear every overlay in one click. Preserve the presence/absence of the optional
  // `konserwator*`/`gddkia`/`closedAreas` keys so maps that don't opt into them keep hiding
  // their checkboxes.
  const disableAll = () =>
    onChange({
      parcels: false,
      infra: false,
      ortho: false,
      mpzp: false,
      gdos: false,
      ...(gugik.konserwator !== undefined ? { konserwator: false } : {}),
      ...(gugik.konserwatorEwidencja !== undefined ? { konserwatorEwidencja: false } : {}),
      ...(gugik.gddkia !== undefined ? { gddkia: false } : {}),
      ...(gugik.closedAreas !== undefined ? { closedAreas: false } : {}),
    });

  // Collapsed: a small square in the same spot. Keeps showing the spinner so overlay activity
  // stays visible while folded; an enabled-layer dot hints something is on without expanding.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => toggle(false)}
        title="Warstwy GUGiK"
        aria-label="Pokaż warstwy GUGiK"
        className={`z-[1000] card bg-[var(--color-bg-surface)] shadow-sm p-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors ${className ?? ""}`}
      >
        <span className="relative block">
          {loading ? <Spinner size="sm" className="w-4 h-4" /> : <Layers size={16} />}
          {anyOn && !loading && (
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[var(--color-primary)]" />
          )}
        </span>
      </button>
    );
  }

  return (
    <div
      className={`z-[1000] card bg-[var(--color-bg-surface)] shadow-sm px-2.5 py-2 space-y-1.5 font-mono text-[11px] text-[var(--color-text-secondary)] ${className ?? ""}`}
    >
      <div className="flex items-center gap-1.5">
        <p className="section-label">Warstwy GUGiK</p>
        {loading && <Spinner size="sm" className="w-3.5 h-3.5" />}
        <button
          type="button"
          onClick={() => toggle(true)}
          title="Zwiń"
          aria-label="Zwiń panel warstw GUGiK"
          className="ml-auto -mr-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        >
          <X size={14} />
        </button>
      </div>
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-[var(--color-primary)]"
          checked={gugik.parcels}
          onChange={(e) => onChange({ ...gugik, parcels: e.target.checked })}
        />
        Działki (granice + numery)
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-[var(--color-primary)]"
          checked={gugik.infra}
          onChange={(e) => onChange({ ...gugik, infra: e.target.checked })}
        />
        Infrastruktura (GESUT)
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-[var(--color-primary)]"
          checked={gugik.ortho}
          onChange={(e) => onChange({ ...gugik, ortho: e.target.checked })}
        />
        Ortofotomapa
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-[var(--color-primary)]"
          checked={gugik.mpzp}
          onChange={(e) => onChange({ ...gugik, mpzp: e.target.checked })}
        />
        MPZP
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-[var(--color-primary)]"
          checked={gugik.gdos}
          onChange={(e) => onChange({ ...gugik, gdos: e.target.checked })}
        />
        Obszary chronione (GDOŚ)
      </label>
      {gugik.konserwator !== undefined && (
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={gugik.konserwator}
            onChange={(e) => onChange({ ...gugik, konserwator: e.target.checked })}
          />
          Rejestr zabytków
        </label>
      )}
      {gugik.konserwatorEwidencja !== undefined && (
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={gugik.konserwatorEwidencja}
            onChange={(e) => onChange({ ...gugik, konserwatorEwidencja: e.target.checked })}
          />
          Ewidencja zabytków
        </label>
      )}
      {identifyHint && (gugik.konserwator || gugik.konserwatorEwidencja) && (
        <p className="text-[10px] text-[var(--color-text-muted)] leading-snug max-w-[170px] normal-case">
          {identifyHint}
        </p>
      )}
      {gugik.konserwator && nidEmpty?.rejestr && (
        <p className="text-[10px] text-amber-600 leading-snug max-w-[170px] normal-case">
          Rejestr zabytków: NID nie ma obiektów w tym widoku.
        </p>
      )}
      {gugik.konserwatorEwidencja && nidEmpty?.ewidencja && (
        <p className="text-[10px] text-amber-600 leading-snug max-w-[170px] normal-case">
          Ewidencja zabytków: NID nie ma obiektów w tym widoku (pokrycie ewidencji jest częściowe).
        </p>
      )}
      {gugik.gddkia !== undefined && (
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={gugik.gddkia}
            onChange={(e) => onChange({ ...gugik, gddkia: e.target.checked })}
          />
          Dane GDDKiA (węzły i pikietaż)
        </label>
      )}
      {gugik.closedAreas !== undefined && (
        <label
          className="flex items-center gap-1.5 cursor-pointer select-none"
          title={closedAreasBasis(closedMeta) || undefined}
        >
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={gugik.closedAreas}
            onChange={(e) => onChange({ ...gugik, closedAreas: e.target.checked })}
          />
          Tereny zamknięte MON{closedUpdated && ` (akt. ${closedUpdated})`}
        </label>
      )}
      {gugik.closedAreas && closedMeta && (
        <p className="text-[10px] text-[var(--color-text-muted)] leading-snug max-w-[170px] normal-case">
          {closedMeta.count
            ? `${closedAreasBasis(closedMeta)}. Linia przerywana — decyzja zamyka tylko część działki.`
            : "Warstwa nie została jeszcze zbudowana."}
        </p>
      )}
      {anyOn && (
        <button
          type="button"
          onClick={disableAll}
          title="Wyłącz wszystkie warstwy GUGiK"
          className="w-full flex items-center gap-1.5 pt-1.5 border-t border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        >
          <EyeOff size={13} />
          Wyłącz wszystkie
        </button>
      )}
      {gugik.parcels && zoom < GUGIK_PARCELS_MIN_ZOOM && (
        <p className="text-[10px] text-amber-600 leading-snug max-w-[170px] normal-case">
          Przybliż — granice działek od ~zoom {GUGIK_PARCELS_MIN_ZOOM}, numery od ~16.
        </p>
      )}
      {gugik.infra && zoom < GUGIK_INFRA_MIN_ZOOM && (
        <p className="text-[10px] text-amber-600 leading-snug max-w-[170px] normal-case">
          Infrastruktura GESUT renderuje się dopiero od ~zoom {GUGIK_INFRA_MIN_ZOOM}.
        </p>
      )}
      {gugik.infra && zoom >= GUGIK_INFRA_MIN_ZOOM && (
        <p className="text-[10px] text-[var(--color-text-muted)] leading-snug max-w-[170px] normal-case">
          Brak sieci = brak danych GESUT dla tego obszaru (nie każdy powiat jest w KGESUT).
        </p>
      )}
      {gugik.mpzp && zoom < GUGIK_MPZP_MIN_ZOOM && (
        <p className="text-[10px] text-amber-600 leading-snug max-w-[170px] normal-case">
          Przybliż — MPZP od ~zoom {GUGIK_MPZP_MIN_ZOOM} (granice planów), treść od ~16, szczegóły od ~17.
        </p>
      )}
      {gugik.konserwatorEwidencja && zoom < GUGIK_KONSERWATOR_EWIDENCJA_MIN_ZOOM && (
        <p className="text-[10px] text-amber-600 leading-snug max-w-[170px] normal-case">
          Przybliż — ewidencja zabytków od ~zoom {GUGIK_KONSERWATOR_EWIDENCJA_MIN_ZOOM}, treść zwykle od ~16.
        </p>
      )}
      {gugik.gddkia && zoom < GDDKIA_HECTOMETRE_ZOOM && (
        <p className="text-[10px] text-[var(--color-text-muted)] leading-snug max-w-[170px] normal-case">
          Słupki co {gddkiaFrequencyForZoom(zoom) === 10000 ? "10 km" : "1 km"} przy tym zoomie —
          co 100 m od ~zoom {GDDKIA_HECTOMETRE_ZOOM}.
        </p>
      )}
    </div>
  );
}

// ── Identify on the NID overlays — object data straight from zabytek.gov.pl ─────────────
// Both NID services answer WMS GetFeatureInfo (every layer queryable="1", CORS `*`; verified
// live 2026-09) in a plain GeoMedia XML:
//   <FeatureCollection><gml:featureMember><Layer Name="…">
//     <Attribute Name="…">value</Attribute>…</Layer></gml:featureMember>…</FeatureCollection>
// application/json is refused (InvalidFormat) and text/html comes pre-styled, so text/xml.
// ZEN attributes are Polish (Nazwa, Funkcja, Chronologia, Status, Forma_ochrony, Data_wpisu,
// Wykaz_dokumentów, Miejscowość, Ulica, Nr_adresowy, Dokładność_położenia, INSPIRE_ID);
// INSPIRE_IMD carries the INSPIRE Protected Sites schema in English (SITENAME,
// NATIONALMONUMENTSRECORD, LEGALFOUNDATIONDOCUMENT, LEGALFOUNDATIONDATE, INSPIREID).
// The scale-band trick from the tile note applies here too: the server picks the ZEN band from
// bbox/width, so all four go in QUERY_LAYERS and exactly one answers.
// `Chronologia` is a period CODE, not a year — the captions come from the NID geoportal's own
// legend config (mapy.zabytek.gov.pl/nid/, "wg chronologii"); the two-letter type code inside
// INSPIRE ids (…NID_E_12_BK.430644 → BK) follows the same legend ("wg funkcji").

export type NidSource = "ewidencja" | "rejestr";

export interface NidMonument {
  source: NidSource;
  inspireId: string | null;
  name: string | null;
  /** Funkcja (ZEN) / INSPIRE national-record category (IMD) — human readable when we can. */
  kind: string | null;
  /** Decoded period caption (ZEN only). */
  chronology: string | null;
  /** Forma ochrony + status (ZEN) / register classification (IMD). */
  protection: string | null;
  address: string | null;
  documents: string | null;
  date: string | null;
  accuracy: string | null;
}

export const NID_SOURCE_LABEL: Record<NidSource, string> = {
  ewidencja: "Ewidencja zabytków",
  rejestr: "Rejestr zabytków",
};

const NID_CHRONOLOGY: Record<string, string> = {
  "0901": "wiek X – poł. wieku XIII",
  "1251": "poł. wieku XIII – wiek XV",
  "1501": "wiek XVI – wiek XVIII",
  "1801": "wiek XIX",
  "1901": "wiek XX",
  brak: "brak datowania",
};

const NID_TYPE_CODE: Record<string, string> = {
  BK: "Budynek",
  BL: "Budowla",
  CM: "Cmentarz",
  KK: "Krajobraz kulturowy",
  MA: "Mała architektura",
  AR: "Zabytek archeologiczny",
  ZA: "Zabytek archeologiczny",
  SK: "Układ komunikacyjny",
  UU: "Układ przestrzenny",
  ZE: "Zespół",
  ZZ: "Zieleń komponowana",
  UN: "Lista Światowego Dziedzictwa UNESCO",
  PH: "Pomnik Historii",
  ZN: "Zabytek nieruchomy",
};

// INSPIRE "NationalMonumentsRecord" values seen on the register service — translated for the
// popup, unknown ones pass through verbatim.
const NID_IMD_RECORD: Record<string, string> = {
  domestic: "mieszkalny",
  religious: "sakralny",
  settlement: "układ osadniczy",
  industrial: "przemysłowy",
  defence: "obronny",
  defensive: "obronny",
  military: "wojskowy",
  funerary: "cmentarny",
  garden: "ogród / park",
  public: "użyteczności publicznej",
  commercial: "handlowy",
  transport: "komunikacyjny",
  agricultural: "gospodarczy / rolniczy",
  monument: "pomnik",
  archaeological: "archeologiczny",
  landscape: "krajobraz kulturowy",
  other: "inny",
};

/** Type caption from the two-letter code inside a NID INSPIRE id, or null. */
export function nidTypeFromInspireId(id: string | null): string | null {
  const m = id?.match(/_([A-Z]{2})\.\d+$/);
  return m ? (NID_TYPE_CODE[m[1]] ?? null) : null;
}

function nidAttr(attrs: Record<string, string>, ...names: string[]): string | null {
  for (const n of names) {
    const v = attrs[n]?.trim();
    if (v) return v;
  }
  return null;
}

function joinParts(parts: (string | null)[], sep: string): string | null {
  const s = parts.filter((p): p is string => !!p).join(sep);
  return s || null;
}

/**
 * Parse one GetFeatureInfo XML answer from a NID service into monuments. Tolerant: a
 * ServiceExceptionReport, HTML, or garbage yields []. Exported for tests.
 */
export function parseNidFeatureInfo(xml: string, source: NidSource): NidMonument[] {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, "text/xml");
  } catch {
    return [];
  }
  if (doc.getElementsByTagName("parsererror").length) return [];
  const out: NidMonument[] = [];
  const layers = doc.getElementsByTagNameNS("*", "Layer");
  for (let i = 0; i < layers.length; i++) {
    const attrs: Record<string, string> = {};
    const nodes = layers[i].getElementsByTagNameNS("*", "Attribute");
    for (let k = 0; k < nodes.length; k++) {
      const name = nodes[k].getAttribute("Name");
      if (name) attrs[name] = nodes[k].textContent ?? "";
    }
    const inspireId = nidAttr(attrs, "INSPIRE_ID", "INSPIREID");
    if (source === "ewidencja") {
      const chron = nidAttr(attrs, "Chronologia");
      out.push({
        source,
        inspireId,
        name: nidAttr(attrs, "Nazwa"),
        kind: nidAttr(attrs, "Funkcja") ?? nidTypeFromInspireId(inspireId),
        chronology: chron ? (NID_CHRONOLOGY[chron] ?? chron) : null,
        protection: joinParts([nidAttr(attrs, "Forma_ochrony"), nidAttr(attrs, "Status")], " · "),
        address: joinParts(
          [
            nidAttr(attrs, "Miejscowość", "Miejscowosc"),
            joinParts([nidAttr(attrs, "Ulica"), nidAttr(attrs, "Nr_adresowy")], " "),
          ],
          ", ",
        ),
        documents: nidAttr(attrs, "Wykaz_dokumentów", "Wykaz_dokumentow"),
        date: nidAttr(attrs, "Data_wpisu"),
        accuracy: nidAttr(attrs, "Dokładność_położenia", "Dokladnosc_polozenia"),
      });
    } else {
      const record = nidAttr(attrs, "NATIONALMONUMENTSRECORD");
      const recordPl = record ? (NID_IMD_RECORD[record.toLowerCase()] ?? record) : null;
      out.push({
        source,
        inspireId,
        name: nidAttr(attrs, "SITENAME"),
        kind: joinParts([nidTypeFromInspireId(inspireId), recordPl], " · "),
        chronology: null,
        protection: "wpisany do rejestru zabytków",
        address: null,
        documents: nidAttr(attrs, "LEGALFOUNDATIONDOCUMENT"),
        date: nidAttr(attrs, "LEGALFOUNDATIONDATE"),
        accuracy: null,
      });
    }
  }
  return out;
}

/**
 * GetFeatureInfo URL for a map click on one NID service. Bbox/size/pixel mirror the current
 * viewport in EPSG:3857 (WMS 1.3.0, x/y order for that CRS) so the server resolves the same
 * scale band the tiles are drawn from. Exported for tests.
 */
export function nidFeatureInfoUrl(
  source: NidSource,
  bounds: L.LatLngBounds,
  size: L.Point,
  pixel: L.Point,
): string {
  const sw = L.CRS.EPSG3857.project(bounds.getSouthWest());
  const ne = L.CRS.EPSG3857.project(bounds.getNorthEast());
  const base = source === "ewidencja" ? KONSERWATOR_EWIDENCJA_WMS : KONSERWATOR_WMS;
  const layers = source === "ewidencja" ? KONSERWATOR_EWIDENCJA_LAYERS : KONSERWATOR_LAYERS;
  const q = new URLSearchParams({
    service: "WMS",
    version: "1.3.0",
    request: "GetFeatureInfo",
    layers,
    query_layers: layers,
    styles: "",
    crs: "EPSG:3857",
    bbox: `${sw.x},${sw.y},${ne.x},${ne.y}`,
    width: String(Math.round(size.x)),
    height: String(Math.round(size.y)),
    i: String(Math.round(pixel.x)),
    j: String(Math.round(pixel.y)),
    info_format: "text/xml",
    feature_count: "10",
  });
  return `${base}?${q.toString()}`;
}

/**
 * Identify the monuments under a map click on the NID overlays that are switched on.
 * Builds the requests synchronously (the viewport may move while awaiting), fetches the
 * services in parallel, and never throws — a failed service just contributes nothing.
 */
export function fetchNidMonuments(
  map: L.Map,
  latlng: L.LatLng,
  sources: { ewidencja?: boolean; rejestr?: boolean },
  signal?: AbortSignal,
): Promise<NidMonument[]> {
  const wanted: NidSource[] = [];
  if (sources.ewidencja) wanted.push("ewidencja");
  if (sources.rejestr) wanted.push("rejestr");
  if (!wanted.length) return Promise.resolve([]);
  const bounds = map.getBounds();
  const size = map.getSize();
  const pixel = map.latLngToContainerPoint(latlng);
  return Promise.all(
    wanted.map(async (source) => {
      try {
        const res = await fetch(nidFeatureInfoUrl(source, bounds, size, pixel), { signal });
        if (!res.ok) return [];
        return parseNidFeatureInfo(await res.text(), source);
      } catch {
        return [];
      }
    }),
  ).then((lists) => lists.flat());
}

const escNid = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Popup section listing identified monuments — plain HTML for Leaflet popups (outside React),
 * styled like the informator's parcel card. Empty list → a one-line "nothing here" note.
 */
export function nidMonumentsHtml(items: NidMonument[]): string {
  // NID mixes "1959-10-01" (ZEN) and "16/05/1966" (INSPIRE), also inside the document list.
  const dates = (val: string | null) =>
    val
      ?.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, "$3.$2.$1")
      .replace(/\b(\d{2})\/(\d{2})\/(\d{4})\b/g, "$1.$2.$3") ?? null;
  const row = (label: string, val: string | null) =>
    val
      ? `<tr>
          <td style="color:#6b7280;padding:1px 10px 1px 0;font-size:10px;vertical-align:top;white-space:nowrap">${escNid(label)}</td>
          <td style="text-align:right;padding:1px 0;font-size:11px;vertical-align:top">${escNid(val)}</td>
        </tr>`
      : "";
  const header = `<div style="font-size:10px;color:#9ca3af;margin:8px 0 4px;padding-top:6px;border-top:1px solid #e5e7eb">Zabytki — NID (zabytek.gov.pl)</div>`;
  if (!items.length) {
    return `${header}<div style="font-size:11px;color:#6b7280">Brak obiektu w tym miejscu.</div>`;
  }
  const cards = items
    .map((m) => {
      const badgeColor = m.source === "rejestr" ? "#b91c1c" : "#a16207";
      return `<div style="margin-bottom:6px;padding:4px 6px;border-left:3px solid ${badgeColor};background:#fafafa">
        <div style="font-size:12px;font-weight:700;color:#111">${escNid(m.name ?? "(bez nazwy)")}</div>
        <div style="font-size:10px;color:${badgeColor};font-weight:600;margin-bottom:2px">${escNid(NID_SOURCE_LABEL[m.source])}</div>
        <table style="width:100%;border-collapse:collapse">
          ${row("Rodzaj", m.kind)}
          ${row("Chronologia", m.chronology)}
          ${row("Ochrona", m.protection)}
          ${row("Adres", m.address)}
          ${row("Dokumenty", dates(m.documents))}
          ${row("Data wpisu", dates(m.date))}
          ${row("Położenie", m.accuracy)}
          ${row("ID", m.inspireId)}
        </table>
      </div>`;
    })
    .join("");
  return `${header}${cards}`;
}
